import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';
import { ChartWrapperInstance, ChartWrapperService, executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';

export interface ZoneScatterAxisSegment {
  label?: string;
  start: number;
  end: number;
  color?: string;
  boundaryColor?: string;
}

export interface ZoneScatterAxisConfig {
  type?: string;
  min: number;
  max: number;
  title?: string;
  segments?: ZoneScatterAxisSegment[];
  axisLabelColor?: string;
  axisLineColor?: string;
  nameGap?: number;
  onZero?: boolean;
}

export interface ZoneScatterZoneConfig {
  id?: string;
  title: string;
  xRange: [number, number];
  yRange: [number, number];
  backgroundColor?: string;
  textColor?: string;
  titlePosition?: 'top-left' | 'top-center' | 'top-right' | 'center' | 'bottom-left' | 'bottom-right';
  fontSize?: number;
  fontWeight?: number | string;
  icon?: string;
}

export interface ZoneScatterPoint {
  id?: string;
  label?: string;
  x: number;
  y: number;
  symbolSize?: number;
  color?: string;
  [key: string]: any;
}

export interface ZoneScatterProjection {
  fromPointId?: string;
  from?: { x: number; y: number };
  to: { x: number; y: number };
  label?: string;
  lineColor?: string;
  targetColor?: string;
  sourcePointId?: string;
}

export interface ZoneScatterResponse {
  points?: ZoneScatterPoint[];
  projections?: ZoneScatterProjection[];
}

@Component({
  selector: 'app-configurable-zone-scatter-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe],
  templateUrl: './configurable-zone-scatter-chart.component.html',
  styleUrl: './configurable-zone-scatter-chart.component.scss',
})
export class ConfigurableZoneScatterChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  protected myChart?: echarts.ECharts;
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private globalFilterService = inject(GlobalFilterService);
  private router = inject(Router);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastResponse?: ZoneScatterResponse;
  private allPointLabels: string[] = [];
  private activeProjectionPointId: string | null = null;

  get subtitle(): string {
    return String(this.data?.chartConfig?.subtitle || this.data?.subtitle || '');
  }

  get showLegend(): boolean {
    const cfg = this.data?.chartConfig || {};
    return cfg?.legend?.show !== false;
  }

  get legendLabel(): string {
    const cfg = this.data?.chartConfig || {};
    const legend = cfg?.legend || {};
    return String(
      legend?.bubbleLabel ||
      legend?.bubble?.label ||
      cfg?.bubbleLegend?.label ||
      legend?.label ||
      'Customer',
    );
  }

  get legendDotStyle(): Record<string, string> {
    const cfg = this.data?.chartConfig || {};
    const legend = cfg?.legend || {};
    const bubbleLegend = cfg?.bubbleLegend || legend?.bubble || {};
    const point = cfg?.point || {};
    const styles = cfg?.styles || {};
    const color = point.color || styles.pointColor || bubbleLegend.color || '#7C8CA3';
    const borderColor = bubbleLegend.borderColor || point.borderColor || styles.pointBorderColor || '#F8FAFC';
    const size = Number(bubbleLegend.size ?? 12);

    return {
      width: `${Number.isFinite(size) ? size : 12}px`,
      height: `${Number.isFinite(size) ? size : 12}px`,
      background: String(color),
      borderColor: String(borderColor),
    };
  }

  get showBubbleScaleLegend(): boolean {
    return false;
  }

  get showBubbleScaleOverlay(): boolean {
    return this.shouldShowBubbleScaleLegend(this.data?.chartConfig || {});
  }

  private shouldShowBubbleScaleLegend(cfg: any): boolean {
    const bubbleLegend = cfg?.bubbleLegend || cfg?.legend?.bubble || {};
    const mode = String(bubbleLegend.mode || bubbleLegend.type || '').toLowerCase();
    return bubbleLegend.show === true && (mode === 'scale' || bubbleLegend.showScale === true);
  }

  get bubbleLegendDescription(): string {
    const cfg = this.data?.chartConfig || {};
    const bubbleLegend = cfg?.bubbleLegend || cfg?.legend?.bubble || {};
    return String(bubbleLegend.description || 'Represents relative revenue volume of the account');
  }

  get bubbleLegendScaleText(): string {
    const cfg = this.data?.chartConfig || {};
    const bubbleLegend = cfg?.bubbleLegend || cfg?.legend?.bubble || {};
    return String(bubbleLegend.scaleLabel || bubbleLegend.label || 'Low -> High revenue');
  }

  get bubbleLegendScaleBubbleSizes(): number[] {
    const cfg = this.data?.chartConfig || {};
    const legend = cfg?.legend || {};
    const bubbleLegend = cfg?.bubbleLegend || legend?.bubble || {};
    const sizes = Array.isArray(bubbleLegend.sizes) && bubbleLegend.sizes.length
      ? bubbleLegend.sizes
      : [10, 18, 30];

    return sizes.slice(0, 3)
      .map((rawSize: any) => Number(rawSize))
      .filter((size: number) => Number.isFinite(size) && size > 0);
  }

  get bubbleLegendScaleStyle(): Record<string, string> {
    const cfg = this.data?.chartConfig || {};
    const legend = cfg?.legend || {};
    const bubbleLegend = cfg?.bubbleLegend || legend?.bubble || {};
    const thresholdCfg = cfg?.stepZones?.thresholdLine || {};
    const point = cfg?.point || {};
    const styles = cfg?.styles || {};
    const top = Number(bubbleLegend.legendTop ?? (Number(thresholdCfg.legendTop ?? 4) + 24));
    const right = Number(bubbleLegend.legendRight ?? thresholdCfg.legendRight ?? 12);
    return {
      '--zone-scatter-bubble-legend-color': String(point.color || styles.pointColor || bubbleLegend.color || '#7C8CA3'),
      '--zone-scatter-bubble-legend-border': String(bubbleLegend.borderColor || point.borderColor || styles.pointBorderColor || '#F8FAFC'),
      '--zone-scatter-bubble-scale-text-color': String(thresholdCfg.legendTextColor || '#475569'),
      top: `${Number.isFinite(top) ? top : 28}px`,
      right: `${Number.isFinite(right) ? right : 12}px`,
    };
  }

  get headerActions(): any[] {
    const actions = this.data?.headerActions || this.data?.chartConfig?.headerActions || [];
    return Array.isArray(actions) ? actions.filter((action) => action?.visible !== false) : [];
  }

  get hasHeaderActions(): boolean {
    return this.headerActions.length > 0;
  }

  trackByHeaderAction = (index: number, action: any): string =>
    String(action?.key || action?.id || action?.label || index);

  trackByBubbleLegendSize = (_index: number, size: number): number => size;

  onHeaderActionClick(action: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!action || action.disabled === true) return;

    const filters = this.globalFilterService.getCurrentFilters?.() || {};
    executeNavigationFromConfig(
      this.router,
      {
        ...(this.data || {}),
        navigateAction: action,
      },
      {
        name: action.label || action.title || action.key || '',
        value: action.value,
        data: {
          raw: action,
        },
        componentType: 'header-action',
      },
      {
        event: {
          name: action.label || action.title || action.key || '',
          value: action.value,
          raw: action,
          componentType: 'header-action',
        },
        data: this.data || {},
        filters,
        context: {
          widgetId: this.data?.widgetId,
          filters,
        },
        widgetId: this.data?.widgetId,
      },
    );
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart || null,
      getSeriesNames: () => [this.legendLabel],
      getCategories: () => this.allPointLabels,
    });

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.resizeChart();
    });

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastResponse || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.bindChartEvents();
      this.renderChart(this.lastResponse);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
      this.bindChartEvents();
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data?.apiUrl, this.data, filters, this.data?.method)?.subscribe({
      next: (response: any) => {
        const normalized = this.normalizeResponse(response);
        this.resizeChart();
        this.renderChart(normalized);
        this.addResizeListener();
      },
      error: () => {
        this.myChart?.hideLoading();
      },
    });
  }

  protected renderChart(response: ZoneScatterResponse): void {
    if (!this.myChart || !this.data) return;

    this.lastResponse = response;

    const cfg = this.getChartConfig();
    const xAxis = this.normalizeAxis(cfg.xAxis, { min: 0, max: 10, title: '' });
    const yAxis = this.normalizeAxis(cfg.yAxis, { min: 0, max: 100, title: '' });
    const zones = this.normalizeZones(cfg, xAxis, yAxis);
    const points = Array.isArray(response.points) ? response.points : [];
    const projections = Array.isArray(response.projections) ? response.projections : [];
    const resolvedProjections = this.resolveProjections(cfg, xAxis, yAxis, points, projections);
    this.activateSinglePointProjection(points, resolvedProjections);
    const projectionCfg = cfg.projection || {};
    const showOnlyDownwardProjections = projectionCfg.showOnlyDownward !== false;
    const computedProjections = resolvedProjections.filter((projection) => {
      if (!this.activeProjectionPointId) return false;
      const sourcePointId = String((projection as any).sourcePointId || projection.fromPointId || '');
      const isActivePoint = sourcePointId === String(this.activeProjectionPointId);
      const isDownward = Number(projection.from?.y) > Number(projection.to?.y);
      return isActivePoint && (!showOnlyDownwardProjections || isDownward);
    });
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const styles = cfg.styles || {};
    const pointStyle = cfg.point || {};
    const grid = cfg.grid || {};
    const resolvedGrid = this.resolveChartGrid(grid, xAxis, yAxis);
    const visiblePointLabels = this.resolveVisiblePointLabels(points, pointStyle, xAxis, yAxis, resolvedGrid);

    this.allPointLabels = points.map((point) => String(point.label || point.id || ''));
    this.wrapper.setSourceData(points, 'label');

    const pointColor = String(pointStyle.color || styles.pointColor || theme.chart?.palette?.[0] || '#94a3b8');
    const pointBorderColor = String(pointStyle.borderColor || styles.pointBorderColor || '#f8fafc');
    const pointBorderWidth = Number(pointStyle.borderWidth ?? styles.pointBorderWidth ?? 1.5);
    const series = [
      this.buildZoneSeries(zones, cfg, xAxis, yAxis),
      this.buildGridLineSeries(xAxis, yAxis, cfg),
      this.buildThresholdLineSeries(cfg, xAxis),
      this.buildProjectionLineSeries(computedProjections, cfg),
      this.buildProjectionLabelSeries(computedProjections, cfg),
      this.buildProjectionPointSeries(computedProjections, cfg),
      {
        name: this.legendLabel,
        type: 'scatter',
        z: 12,
        data: points.map((point, index) => {
          const displayLabel = String(point.label || point.id || '');
          const labelKey = this.getPointLabelKey(point, index);
          return {
            name: displayLabel,
            value: [Number(point.x), Number(point.y)],
            ...point,
            raw: point,
            rawLabel: displayLabel,
            labelKey,
            zoneScatterRole: 'customer-point',
            itemStyle: {
              color: point.color || pointColor,
              borderColor: point['borderColor'] || pointBorderColor,
              borderWidth: Number(point['borderWidth'] ?? pointBorderWidth),
              shadowBlur: Number(pointStyle.shadowBlur ?? 4),
              shadowColor: pointStyle.shadowColor || 'rgba(15, 23, 42, 0.16)',
            },
            symbolSize: Number(point.symbolSize || pointStyle.symbolSize || 12),
            label: {
              show: pointStyle.showLabels !== false && visiblePointLabels.has(labelKey),
              position: this.resolvePointLabelPosition(point, index, pointStyle),
              distance: Number(pointStyle.labelDistance ?? 6),
              color: pointStyle.labelColor || styles.labelColor || '#334155',
              fontSize: Number(pointStyle.labelFontSize || 11),
              fontWeight: pointStyle.labelFontWeight || 500,
              width: pointStyle.labelWidth ? Number(pointStyle.labelWidth) : undefined,
              overflow: pointStyle.labelOverflow || 'truncate',
            },
          };
        }),
        label: {
          show: pointStyle.showLabels !== false,
          formatter: (params: any) => {
            const data = params?.data || {};
            return visiblePointLabels.has(String(data.labelKey || data.id || params.dataIndex || ''))
              ? String(data.rawLabel || data.id || '')
              : '';
          },
          position: pointStyle.labelPosition || 'right',
          distance: Number(pointStyle.labelDistance ?? 8),
          color: pointStyle.labelColor || styles.labelColor || '#334155',
          fontSize: Number(pointStyle.labelFontSize || 11),
          fontWeight: pointStyle.labelFontWeight || 500,
          width: pointStyle.labelWidth ? Number(pointStyle.labelWidth) : undefined,
          overflow: pointStyle.labelOverflow || 'truncate',
        },
        labelLayout: pointStyle.labelLayout === false
          ? undefined
          : {
              hideOverlap: pointStyle.useEchartsHideOverlap === true,
              moveOverlap: pointStyle.moveOverlap || 'shiftY',
            },
        emphasis: { focus: 'series' },
      },
    ].filter(Boolean);

    const option: any = {
      animation: cfg.animation !== false,
      tooltip: this.buildTooltip(cfg, theme),
      legend: this.buildChartLegend(cfg),
      graphic: [],
      grid: {
        ...resolvedGrid,
      },
      xAxis: {
        type: xAxis.type || 'value',
        min: xAxis.min,
        max: xAxis.max,
        name: xAxis.title || '',
        nameLocation: cfg.xAxis?.nameLocation || 'middle',
        nameGap: xAxis.nameGap ?? 34,
        axisLine: {
          onZero: xAxis.onZero ?? cfg.xAxis?.onZero ?? false,
          lineStyle: { color: xAxis.axisLineColor || styles.axisLineColor || '#cbd5e1' },
        },
        axisTick: { show: cfg.axisTicks?.show !== false },
        axisLabel: { color: xAxis.axisLabelColor || styles.axisLabelColor || '#64748b' },
        splitLine: { show: false },
      },
      yAxis: {
        type: yAxis.type || 'value',
        min: yAxis.min,
        max: yAxis.max,
        name: yAxis.title || '',
        nameLocation: cfg.yAxis?.nameLocation || 'middle',
        nameGap: yAxis.nameGap ?? 44,
        axisLine: {
          onZero: yAxis.onZero ?? cfg.yAxis?.onZero ?? false,
          lineStyle: { color: yAxis.axisLineColor || styles.axisLineColor || '#cbd5e1' },
        },
        axisTick: { show: cfg.axisTicks?.show !== false },
        axisLabel: { color: yAxis.axisLabelColor || styles.axisLabelColor || '#64748b' },
        splitLine: { show: false },
      },
      series,
    };

    try {
          applyRoleVisibilityToOption(option, this.data);
      this.myChart.setOption(option, true);
      this.wrapper.afterRender();
      requestAnimationFrame(() => this.resizeChart());
    } finally {
      this.myChart.hideLoading();
    }
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.resizeChart();
  };

  private resizeChart(): void {
    if (!this.myChart) return;
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;
    const rect = chartDom.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    this.myChart.resize({ width: rect.width, height: rect.height });
  }

  private resolveChartGrid(grid: any, xAxis: ZoneScatterAxisConfig, yAxis: ZoneScatterAxisConfig): any {
    const containLabel = grid.containLabel !== false;
    const hasXAxisTitle = !!String(xAxis.title || '').trim();
    const hasYAxisTitle = !!String(yAxis.title || '').trim();
    const xNameGap = Number(xAxis.nameGap ?? 34);
    const yNameGap = Number(yAxis.nameGap ?? 44);
    const minLeft = containLabel ? Math.max(58, hasYAxisTitle ? yNameGap + 22 : 50) : 46;
    const minRight = containLabel ? 34 : 12;
    const minTop = containLabel ? 18 : 8;
    const minBottom = containLabel ? Math.max(48, hasXAxisTitle ? xNameGap + 24 : 38) : 34;
    const configuredLeft = Number(grid.left);
    const configuredRight = Number(grid.right);
    const configuredTop = Number(grid.top);
    const configuredBottom = Number(grid.bottom);

    return {
      left: Math.max(Number.isFinite(configuredLeft) ? configuredLeft : minLeft, minLeft),
      right: Math.max(Number.isFinite(configuredRight) ? configuredRight : minRight, minRight),
      top: Math.max(Number.isFinite(configuredTop) ? configuredTop : minTop, minTop),
      bottom: Math.max(Number.isFinite(configuredBottom) ? configuredBottom : minBottom, minBottom),
      containLabel,
    };
  }

  private resolveVisiblePointLabels(
    points: ZoneScatterPoint[],
    pointStyle: any,
    xAxis: ZoneScatterAxisConfig,
    yAxis: ZoneScatterAxisConfig,
    grid: any,
  ): Set<string> {
    const visible = new Set<string>();
    if (pointStyle.showLabels === false) return visible;
    if (pointStyle.hideOverlappingLabels === false) {
      points.forEach((point, index) => visible.add(this.getPointLabelKey(point, index)));
      return visible;
    }

    const chartWidth = this.myChart?.getWidth?.() || 0;
    const chartHeight = this.myChart?.getHeight?.() || 0;
    if (!chartWidth || !chartHeight) {
      points.forEach((point, index) => visible.add(this.getPointLabelKey(point, index)));
      return visible;
    }

    const gridLeft = this.resolveGridSize(grid.left, 46);
    const gridRight = this.resolveGridSize(grid.right, 12);
    const gridTop = this.resolveGridSize(grid.top, 8);
    const gridBottom = this.resolveGridSize(grid.bottom, 34);
    const plotWidth = Math.max(1, chartWidth - gridLeft - gridRight);
    const plotHeight = Math.max(1, chartHeight - gridTop - gridBottom);
    const fontSize = Number(pointStyle.labelFontSize || 11);
    const labelWidth = Number(pointStyle.labelWidth || 120);
    const labelPadding = Number(pointStyle.labelCollisionPadding ?? 6);
    const occupied: Array<{ x: number; y: number; width: number; height: number }> = [];

    const labelCandidates = points
      .map((point, index) => ({ point, index }))
      .sort((a, b) => this.getLabelPlacementPriority(b.point, pointStyle) - this.getLabelPlacementPriority(a.point, pointStyle));

    labelCandidates.forEach(({ point, index }) => {
      const x = Number(point.x);
      const y = Number(point.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      const pixelX = gridLeft + ((x - xAxis.min) / Math.max(1, xAxis.max - xAxis.min)) * plotWidth;
      const pixelY = gridTop + ((yAxis.max - y) / Math.max(1, yAxis.max - yAxis.min)) * plotHeight;
      const positions = this.resolvePointLabelPositionCandidates(point, index, pointStyle);

      for (const position of positions) {
        const paddedBox = this.findAvailableLabelBox(
          point,
          index,
          pointStyle,
          pixelX,
          pixelY,
          fontSize,
          labelWidth,
          labelPadding,
          position,
          chartWidth,
          chartHeight,
          occupied,
        );

        if (paddedBox) {
          point['resolvedLabelPosition'] = position;
          visible.add(this.getPointLabelKey(point, index));
          occupied.push(paddedBox);
          break;
        }
      }
    });

    return visible;
  }

  private getLabelPlacementPriority(point: ZoneScatterPoint, pointStyle: any): number {
    const symbolSize = Number(point.symbolSize || pointStyle.symbolSize || 12);
    const labelLength = String(point.label || point.id || '').length;
    return (Number.isFinite(symbolSize) ? symbolSize * 10 : 0) - Math.min(labelLength, 40);
  }

  private findAvailableLabelBox(
    point: ZoneScatterPoint,
    index: number,
    pointStyle: any,
    pixelX: number,
    pixelY: number,
    fontSize: number,
    configuredWidth: number,
    labelPadding: number,
    position: string,
    chartWidth: number,
    chartHeight: number,
    occupied: Array<{ x: number; y: number; width: number; height: number }>,
  ): { x: number; y: number; width: number; height: number } | null {
    const label = String(point.label || point.id || '');
    const naturalWidth = Math.max(24, label.length * fontSize * 0.58);
    const minFitWidth = Math.max(32, Math.min(naturalWidth, Number(pointStyle.minLabelWidth ?? 58)));
    const widths = Array.from(new Set([
      Math.min(configuredWidth, naturalWidth),
      Math.min(configuredWidth, Math.max(minFitWidth, configuredWidth * 0.75)),
      minFitWidth,
    ].filter((value) => Number.isFinite(value) && value > 0)));

    for (const width of widths) {
      const box = this.resolveLabelBox(point, index, pointStyle, pixelX, pixelY, fontSize, width, position);
      const paddedBox = {
        x: box.x - labelPadding,
        y: box.y - labelPadding,
        width: box.width + labelPadding * 2,
        height: box.height + labelPadding * 2,
      };

      const isInsideChart =
        paddedBox.x >= 0 &&
        paddedBox.y >= 0 &&
        paddedBox.x + paddedBox.width <= chartWidth &&
        paddedBox.y + paddedBox.height <= chartHeight;
      const overlaps = occupied.some((existing) => this.rectsOverlap(existing, paddedBox));

      if (isInsideChart && !overlaps) {
        return paddedBox;
      }
    }

    return null;
  }

  private resolveLabelBox(
    point: ZoneScatterPoint,
    index: number,
    pointStyle: any,
    pixelX: number,
    pixelY: number,
    fontSize: number,
    configuredWidth: number,
    resolvedPosition?: string,
  ): { x: number; y: number; width: number; height: number } {
    const label = String(point.label || point.id || '');
    const symbolSize = Number(point.symbolSize || pointStyle.symbolSize || 12);
    const distance = Number(pointStyle.labelDistance ?? 6);
    const position = resolvedPosition || this.resolvePointLabelPosition(point, index, pointStyle);
    const width = Math.min(configuredWidth, Math.max(24, label.length * fontSize * 0.58));
    const height = Math.max(14, fontSize * 1.35);
    const offset = symbolSize / 2 + distance;

    switch (position) {
      case 'left':
        return { x: pixelX - offset - width, y: pixelY - height / 2, width, height };
      case 'top':
        return { x: pixelX - width / 2, y: pixelY - offset - height, width, height };
      case 'bottom':
        return { x: pixelX - width / 2, y: pixelY + offset, width, height };
      case 'right':
      default:
        return { x: pixelX + offset, y: pixelY - height / 2, width, height };
    }
  }

  private rectsOverlap(
    a: { x: number; y: number; width: number; height: number },
    b: { x: number; y: number; width: number; height: number },
  ): boolean {
    return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
  }

  private getPointLabelKey(point: ZoneScatterPoint, index: number): string {
    return String(point.id || point.label || index);
  }

  private resolveGridSize(value: any, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value.replace('px', '').trim());
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  }

  private resolvePointLabelPosition(point: ZoneScatterPoint, index: number, pointStyle: any): string {
    const resolvedPointPosition = String(point['resolvedLabelPosition'] || '').trim();
    if (resolvedPointPosition) return resolvedPointPosition;

    const configuredPointPosition = String(point['labelPosition'] || '').trim();
    if (configuredPointPosition) return configuredPointPosition;

    const positions = Array.isArray(pointStyle.labelPositions)
      ? pointStyle.labelPositions.map((item: any) => String(item || '').trim()).filter(Boolean)
      : [];

    if (pointStyle.autoLabelPosition === true && positions.length > 0) {
      return positions[index % positions.length];
    }

    return String(pointStyle.labelPosition || 'right');
  }

  private resolvePointLabelPositionCandidates(point: ZoneScatterPoint, index: number, pointStyle: any): string[] {
    const configuredPointPosition = String(point['labelPosition'] || '').trim();
    if (configuredPointPosition) return [configuredPointPosition];

    const positions = Array.isArray(pointStyle.labelPositions)
      ? pointStyle.labelPositions.map((item: any) => String(item || '').trim()).filter(Boolean)
      : [];
    const preferred = String(pointStyle.labelPosition || 'right').trim() || 'right';
    const candidates = pointStyle.autoLabelPosition === true && positions.length > 0
      ? [positions[index % positions.length], preferred, ...positions, 'right', 'left', 'top', 'bottom']
      : [preferred, 'right', 'left', 'top', 'bottom'];

    return Array.from(new Set(candidates.filter(Boolean)));
  }

  private buildTooltip(cfg: any, theme: any): any {
    const tooltip = cfg.tooltip || {};
    return {
      show: tooltip.show !== false,
      trigger: 'item',
      backgroundColor: tooltip.backgroundColor || theme.tooltip?.bg || '#ffffff',
      borderColor: tooltip.borderColor || theme.tooltip?.borderColor || '#cbd5e1',
      textStyle: {
        color: tooltip.textColor || theme.tooltip?.textColor || '#0f172a',
        fontSize: Number(tooltip.fontSize || theme.tooltip?.fontSize || 12),
      },
      formatter: (params: any) => {
        const point = params?.data || {};
        if (!Array.isArray(point.value)) return '';
        const label = point.rawLabel || point.label || point.id || params.seriesName || '';
        const xLabel = cfg.xAxis?.title || 'X';
        const yLabel = cfg.yAxis?.title || 'Y';
        const equipmentKey = String(tooltip.equipmentKey || 'equipment_count');
        const equipmentValue = point?.[equipmentKey] ?? point?.equipment_count ?? point?.equipmentCount;
        const xValue = Number(point.value[0]);
        const yValue = Number(point.value[1]);
        const xDecimals = Number.isFinite(Number(tooltip.xDecimals))
          ? Math.max(0, Number(tooltip.xDecimals))
          : 2;
        const hasExplicitYDecimals = Number.isFinite(Number(tooltip.yDecimals));
        const yDecimals = hasExplicitYDecimals
          ? Math.max(0, Number(tooltip.yDecimals))
          : /discount/i.test(String(yLabel))
            ? 0
            : null;
        const formattedX = Number.isFinite(xValue)
          ? this.formatTooltipNumber(xValue, xDecimals)
          : point.value[0];
        const formattedY = Number.isFinite(yValue) && yDecimals !== null
          ? this.formatTooltipNumber(yValue, yDecimals)
          : point.value[1];
        const equipmentLine =
          equipmentValue !== undefined && equipmentValue !== null && String(equipmentValue).trim() !== ''
            ? `<br/>Equipment: ${equipmentValue}`
            : '';
        return `${label}<br/>${xLabel}: ${formattedX}<br/>${yLabel}: ${formattedY}${equipmentLine}`;
      },
    };
  }

  private formatTooltipNumber(value: number, decimals: number): string {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: Math.max(0, decimals),
    });
  }

  private buildZoneSeries(
    zones: ZoneScatterZoneConfig[],
    cfg: any = {},
    xAxis?: ZoneScatterAxisConfig,
    yAxis?: ZoneScatterAxisConfig,
  ): any {
    const zoneBleed = cfg.zoneBleed || cfg.zoneBackgroundBleed || {};
    return {
      type: 'custom',
      name: 'Zones',
      coordinateSystem: 'cartesian2d',
      silent: true,
      clip: false,
      z: 1,
      data: zones.map((_zone, index) => [index]),
      renderItem: (_params: any, api: any) => {
        const zone = zones[api.value(0)] || {};
        const xRange = zone.xRange || [0, 0];
        const yRange = zone.yRange || [0, 0];
        const p1 = api.coord([xRange[0], yRange[0]]);
        const p2 = api.coord([xRange[1], yRange[1]]);
        let x = Math.min(p1[0], p2[0]);
        let y = Math.min(p1[1], p2[1]);
        let right = Math.max(p1[0], p2[0]);
        let bottom = Math.max(p1[1], p2[1]);
        const chartWidth = typeof api.getWidth === 'function' ? Number(api.getWidth()) : right;
        const chartHeight = typeof api.getHeight === 'function' ? Number(api.getHeight()) : bottom;

        if (zoneBleed.left === true && xAxis && Number(xRange[0]) <= Number(xAxis.min)) {
          x = 0;
        }
        if (zoneBleed.right === true && xAxis && Number(xRange[1]) >= Number(xAxis.max)) {
          right = Number.isFinite(chartWidth) ? chartWidth : right;
        }
        if (zoneBleed.top === true && yAxis && Number(yRange[1]) >= Number(yAxis.max)) {
          y = 0;
        }
        if (zoneBleed.bottom === true && yAxis && Number(yRange[0]) <= Number(yAxis.min)) {
          bottom = Number.isFinite(chartHeight) ? chartHeight : bottom;
        }

        const width = Math.max(0, right - x);
        const height = Math.max(0, bottom - y);
        const titlePoint = this.resolveZoneTitlePoint(zone, x, y, width, height);
        return {
          type: 'group',
          children: [
            {
              type: 'rect',
              shape: { x, y, width, height },
              style: { fill: zone.backgroundColor || 'rgba(241,245,249,0.5)' },
            },
            {
              type: 'text',
              silent: true,
              style: {
                x: titlePoint.x,
                y: titlePoint.y,
                text: `${zone.icon ? `${zone.icon} ` : ''}${zone.title || ''}`,
                fill: zone.textColor || '#334155',
                font: `${zone.fontWeight || 800} ${zone.fontSize || 14}px sans-serif`,
                textAlign: titlePoint.align,
                textVerticalAlign: 'top',
              },
            },
          ],
        };
      },
    };
  }

  private buildGridLineSeries(xAxis: ZoneScatterAxisConfig, yAxis: ZoneScatterAxisConfig, cfg: any): any {
    if (cfg.gridLines?.show === false) {
      return null;
    }

    const styles = cfg.styles || {};
    const lineColor = String(styles.gridLineColor || '#e2e8f0');
    const labels: any[] = [];
    const lines: any[] = [];

    this.getAxisBoundaries(xAxis).forEach((x) => {
      lines.push({ from: [x, yAxis.min], to: [x, yAxis.max], color: lineColor });
    });
    this.getAxisBoundaries(yAxis).forEach((y) => {
      lines.push({ from: [xAxis.min, y], to: [xAxis.max, y], color: lineColor });
    });

    (xAxis.segments || []).forEach((segment) => {
      labels.push({
        value: [(segment.start + segment.end) / 2, yAxis.min],
        label: segment.label || '',
        axis: 'x',
      });
    });
    (yAxis.segments || []).forEach((segment) => {
      labels.push({
        value: [xAxis.min, (segment.start + segment.end) / 2],
        label: segment.label || '',
        axis: 'y',
      });
    });

    return {
      type: 'custom',
      name: 'Grid',
      coordinateSystem: 'cartesian2d',
      silent: true,
      z: 4,
      data: [0],
      renderItem: (_params: any, api: any) => {
        const children: any[] = [];
        lines.forEach((line: any) => {
          const from = api.coord(line.from);
          const to = api.coord(line.to);
          children.push({
            type: 'line',
            shape: { x1: from[0], y1: from[1], x2: to[0], y2: to[1] },
            style: { stroke: line.color, lineWidth: Number(styles.gridLineWidth || 1) },
          });
        });
        labels.forEach((label: any) => {
          const point = api.coord(label.value);
          children.push({
            type: 'text',
            style: {
              x: label.axis === 'x' ? point[0] : point[0] - 12,
              y: label.axis === 'x' ? point[1] + 24 : point[1],
              text: label.label,
              fill: styles.segmentLabelColor || '#64748b',
              font: `${Number(styles.segmentLabelFontSize || 11)}px sans-serif`,
              textAlign: label.axis === 'x' ? 'center' : 'right',
              textVerticalAlign: 'middle',
            },
          });
        });
        return { type: 'group', children };
      },
    };
  }

  private buildBubbleScaleLegendGraphic(cfg: any): any[] {
    if (!this.shouldShowBubbleScaleLegend(cfg)) {
      return [];
    }

    const bubbleLegend = cfg?.bubbleLegend || cfg?.legend?.bubble || {};
    const thresholdCfg = cfg?.stepZones?.thresholdLine || {};
    const point = cfg?.point || {};
    const styles = cfg?.styles || {};
    const sizes = Array.isArray(bubbleLegend.sizes) && bubbleLegend.sizes.length
      ? bubbleLegend.sizes
      : [8, 14, 22];
    const resolvedSizes = sizes
      .slice(0, 3)
      .map((value: any) => Number(value))
      .filter((value: number) => Number.isFinite(value) && value > 0);
    if (!resolvedSizes.length) {
      return [];
    }

    const color = String(point.color || styles.pointColor || bubbleLegend.color || '#7C8CA3');
    const borderColor = String(bubbleLegend.borderColor || point.borderColor || styles.pointBorderColor || '#F8FAFC');
    const legendTextColor = String(thresholdCfg.legendTextColor || '#475569');
    const description = String(bubbleLegend.description || 'Represents relative revenue volume of the account');
    const scaleLabel = String(bubbleLegend.scaleLabel || bubbleLegend.label || 'Low -> High revenue');
    const fontSize = Number(bubbleLegend.fontSize ?? 10);
    const top = Number(bubbleLegend.legendTop ?? (Number(thresholdCfg.legendTop ?? 4) + 24));
    const right = Number(bubbleLegend.legendRight ?? thresholdCfg.legendRight ?? 12);
    const rowTop = fontSize + 8;
    let cursorX = 0;

    const bubbleChildren: any[] = resolvedSizes.map((size: number) => {
      const radius = size / 2;
      const child = {
        type: 'circle',
        shape: {
          cx: cursorX + radius,
          cy: rowTop + Math.max(...resolvedSizes) / 2,
          r: radius,
        },
        style: {
          fill: color,
          stroke: borderColor,
          lineWidth: Number(bubbleLegend.borderWidth ?? point.borderWidth ?? 1.5),
          opacity: 1,
          shadowBlur: 2,
          shadowColor: 'rgba(15, 23, 42, 0.18)',
        },
      };
      cursorX += size + Number(bubbleLegend.bubbleGap ?? 8);
      return child;
    });

    bubbleChildren.push({
      type: 'text',
      style: {
        x: cursorX + 4,
        y: rowTop + Math.max(...resolvedSizes) / 2,
        text: scaleLabel,
        fill: legendTextColor,
        stroke: legendTextColor,
        textFill: legendTextColor,
        textStroke: legendTextColor,
        textStrokeWidth: 0,
        fontSize,
        fontWeight: bubbleLegend.fontWeight || thresholdCfg.legendFontWeight || 600,
        opacity: 1,
        textAlign: 'left',
        textVerticalAlign: 'middle',
      },
    } as any);

    return [
      {
        type: 'group',
        right: Number.isFinite(right) ? right : 12,
        top: Number.isFinite(top) ? top : 28,
        z: 100,
        zlevel: 10,
        silent: true,
        children: [
          {
            type: 'text',
            style: {
              x: 0,
              y: 0,
              text: description,
              fill: legendTextColor,
              stroke: legendTextColor,
              textFill: legendTextColor,
              textStroke: legendTextColor,
              textStrokeWidth: 0,
              fontSize,
              fontWeight: bubbleLegend.descriptionFontWeight || thresholdCfg.legendFontWeight || 600,
              opacity: 1,
              textAlign: 'left',
              textVerticalAlign: 'top',
            },
          },
          ...bubbleChildren,
        ],
      },
    ];
  }

  private buildChartLegend(cfg: any): any {
    const stepZones = cfg?.stepZones;
    const thresholdCfg = cfg?.stepZones?.thresholdLine || {};
    const bubbleLegend = cfg?.bubbleLegend || cfg?.legend?.bubble || {};
    const bubbleLegendMode = String(bubbleLegend.mode || bubbleLegend.type || '').toLowerCase();
    const showScaleBubbleLegend = bubbleLegend.show === true && (bubbleLegendMode === 'scale' || bubbleLegend.showScale === true);
    const showBubbleLegend = !showScaleBubbleLegend && (bubbleLegend.show === true || cfg?.legend?.showBubble === true);
    const showThresholdLegend = stepZones?.enabled === true && thresholdCfg.show !== false && thresholdCfg.showLegend !== false;

    if (!showBubbleLegend && !showThresholdLegend) {
      return { show: false };
    }

    const thresholdName = String(thresholdCfg.name || thresholdCfg.label || 'Suggested Discount Threshold');
    const thresholdColor = thresholdCfg.color || '#15803D';
    const bubbleName = String(bubbleLegend.label || cfg?.legend?.bubbleLabel || cfg?.legend?.label || 'Revenue');
    const bubbleColor = String(cfg?.point?.color || cfg?.styles?.pointColor || bubbleLegend.color || '#7C8CA3');
    const data: any[] = [];

    if (showBubbleLegend) {
      data.push({
        name: bubbleName,
        icon: bubbleLegend.icon || 'circle',
        itemStyle: {
          color: bubbleColor,
          borderColor: bubbleLegend.borderColor || cfg?.point?.borderColor || '#F8FAFC',
          borderWidth: Number(bubbleLegend.borderWidth ?? cfg?.point?.borderWidth ?? 1.5),
        },
      });
    }

    if (showThresholdLegend) {
      data.push({
        name: thresholdName,
        icon: thresholdCfg.legendIcon || this.buildDottedLegendIcon(thresholdColor),
      });
    }

    return {
      show: true,
      data,
      top: thresholdCfg.legendTop ?? 4,
      right: thresholdCfg.legendRight ?? 12,
      itemWidth: Number(thresholdCfg.legendItemWidth ?? 16),
      itemHeight: Number(thresholdCfg.legendItemHeight ?? 12),
      textStyle: {
        color: thresholdCfg.legendTextColor || '#475569',
        fontSize: Number(thresholdCfg.legendFontSize ?? 11),
        fontWeight: thresholdCfg.legendFontWeight || 600,
      },
    };
  }

  private buildDottedLegendIcon(color: string): string {
    const encodedColor = encodeURIComponent(color || '#15803D');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="8" viewBox="0 0 34 8"><line x1="1" y1="4" x2="33" y2="4" stroke="${encodedColor}" stroke-width="2" stroke-linecap="round" stroke-dasharray="1 5"/></svg>`;
    return `image://data:image/svg+xml;utf8,${svg}`;
  }

  private buildThresholdLineSeries(cfg: any, xAxis: ZoneScatterAxisConfig): any {
    const stepZones = cfg?.stepZones;
    const thresholdCfg = stepZones?.thresholdLine || {};
    if (stepZones?.enabled !== true || thresholdCfg.show === false || !Array.isArray(stepZones?.steps)) {
      return null;
    }

    const data: Array<{ start: number; end: number; y: number }> = [];
    stepZones.steps.forEach((step: any, index: number) => {
      const range = Array.isArray(step?.xRange) ? step.xRange : null;
      const y = Number(step?.safeYEnd ?? step?.yEnd ?? step?.threshold);
      if (!range || range.length < 2 || !Number.isFinite(y)) return;

      const start = Number(range[0]);
      const end = Number(range[1]);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return;

      data.push({ start, end, y });
    });

    if (!data.length) return null;

    const thresholdName = String(thresholdCfg.name || thresholdCfg.label || 'Suggested Discount Threshold');
    const zoneBleed = cfg.zoneBleed || cfg.zoneBackgroundBleed || {};
    const lineDash = this.resolveLineDash(thresholdCfg.type || 'dotted', Number(thresholdCfg.width ?? 2));

    return {
      name: thresholdName,
      type: 'custom',
      coordinateSystem: 'cartesian2d',
      z: Number(thresholdCfg.z ?? 7),
      silent: thresholdCfg.silent !== false,
      data: data.map((line) => [line.start, line.end, line.y]),
      renderItem: (_params: any, api: any) => {
        const index = Number(_params?.dataIndex ?? 0);
        const current = data[index];
        if (!current) return null;

        const from = api.coord([current.start, current.y]);
        const to = api.coord([current.end, current.y]);
        const chartWidth = typeof api.getWidth === 'function' ? Number(api.getWidth()) : to[0];
        let x1 = from[0];
        let x2 = to[0];

        if (zoneBleed.left === true && current.start <= Number(xAxis.min)) {
          x1 = 0;
        }
        if (zoneBleed.right === true && current.end >= Number(xAxis.max)) {
          x2 = Number.isFinite(chartWidth) ? chartWidth : x2;
        }

        const children: any[] = [
          {
            type: 'line',
            shape: { x1, y1: from[1], x2, y2: to[1] },
            style: {
              stroke: thresholdCfg.color || '#15803D',
              lineWidth: Number(thresholdCfg.width ?? 2),
              lineDash,
            },
          },
        ];

        const next = data[index + 1];
        if (next && Number(next.start) === Number(current.end) && Number(next.y) !== Number(current.y)) {
          const verticalFrom = api.coord([current.end, current.y]);
          const verticalTo = api.coord([next.start, next.y]);
          children.push({
            type: 'line',
            shape: { x1: verticalFrom[0], y1: verticalFrom[1], x2: verticalTo[0], y2: verticalTo[1] },
            style: {
              stroke: thresholdCfg.color || '#15803D',
              lineWidth: Number(thresholdCfg.width ?? 2),
              lineDash,
            },
          });
        }

        return {
          type: 'group',
          children,
        };
      },
      emphasis: { disabled: true },
      tooltip: {
        show: false,
      },
    };
  }

  private resolveLineDash(type: string, width: number): number[] | undefined {
    const normalized = String(type || '').toLowerCase();
    if (normalized === 'solid') return undefined;
    if (normalized === 'dashed') return [Math.max(4, width * 4), Math.max(3, width * 2)];
    return [Math.max(1, width), Math.max(3, width * 2)];
  }

  private buildProjectionLineSeries(projections: Array<ZoneScatterProjection & { from: { x: number; y: number }; to: { x: number; y: number }; label: string }>, cfg: any): any {
    const projectionCfg = cfg.projection || {};
    const data = projections
      .map((projection) => ({
        value: [projection.from.x, projection.from.y, projection.to.x, projection.to.y],
        lineColor: projection.lineColor || projectionCfg.lineColor || '#94a3b8',
      }))
      .filter((item) => item.value.every((value: number) => Number.isFinite(value)));

    return {
      type: 'custom',
      name: 'Projection',
      coordinateSystem: 'cartesian2d',
      z: 9,
      silent: true,
      data,
      renderItem: (_params: any, api: any) => {
        const from = api.coord([api.value(0), api.value(1)]);
        const to = api.coord([api.value(2), api.value(3)]);
        const dx = to[0] - from[0];
        const dy = to[1] - from[1];
        const length = Math.hypot(dx, dy) || 1;
        const ux = dx / length;
        const uy = dy / length;
        const arrowSize = Number(projectionCfg.arrowSize || 10);
        const lineWidth = Number(projectionCfg.lineWidth || 1.5);
        const sourceGap = Math.max(0, Number(projectionCfg.sourceGap ?? projectionCfg.pointGap ?? 11));
        const color = api.style({}).fill || data[_params.dataIndex]?.lineColor || projectionCfg.lineColor || '#94a3b8';
        const lineStart = [from[0] + ux * sourceGap, from[1] + uy * sourceGap];
        const arrowTip = [to[0], to[1]];
        const arrowBase = [to[0] - ux * arrowSize, to[1] - uy * arrowSize];
        const perpX = -uy;
        const perpY = ux;

        return {
          type: 'group',
          children: [
            {
              type: 'line',
              shape: { x1: lineStart[0], y1: lineStart[1], x2: to[0], y2: to[1] },
              style: {
                stroke: color,
                lineWidth,
                lineDash: projectionCfg.lineType === 'solid' ? undefined : [6, 5],
              },
            },
            {
              type: 'polygon',
              shape: {
                points: [
                  arrowTip,
                  [arrowBase[0] + perpX * arrowSize * 0.45, arrowBase[1] + perpY * arrowSize * 0.45],
                  [arrowBase[0] - perpX * arrowSize * 0.45, arrowBase[1] - perpY * arrowSize * 0.45],
                ],
              },
              style: { fill: color },
            },
          ],
        };
      },
    };
  }

  private buildProjectionLabelSeries(projections: Array<ZoneScatterProjection & { from: { x: number; y: number }; to: { x: number; y: number }; label: string }>, cfg: any): any {
    const projectionCfg = cfg.projection || {};
    const labelColor = projectionCfg.labelColor || '#d97706';
    return {
      type: 'scatter',
      name: 'Projection Label',
      z: 13,
      silent: true,
      symbolSize: 1,
      itemStyle: { color: 'transparent', opacity: 0 },
      tooltip: { show: false },
      data: projections
        .map((projection) => ({
          value: [(projection.from.x + projection.to.x) / 2, (projection.from.y + projection.to.y) / 2],
          label: projection.label,
        }))
        .filter((item) => Number.isFinite(item.value?.[0]) && Number.isFinite(item.value?.[1])),
      label: {
        show: projectionCfg.showLabels !== false,
        formatter: (params: any) => String(params.data?.label || ''),
        position: 'right',
        offset: [10, 0],
        color: labelColor,
        backgroundColor: projectionCfg.labelBackgroundColor || '#ffffff',
        borderColor: projectionCfg.labelBorderColor || labelColor,
        borderWidth: projectionCfg.labelBorderWidth ?? 1,
        borderRadius: projectionCfg.labelBorderRadius ?? 4,
        padding: projectionCfg.labelPadding || [3, 5],
        align: 'left',
        verticalAlign: 'middle',
      },
    };
  }

  private buildProjectionPointSeries(projections: ZoneScatterProjection[], cfg: any): any {
    const projectionCfg = cfg.projection || {};
    if (projectionCfg.showTargetPoint === false) {
      return null;
    }

    return {
      type: 'scatter',
      name: 'Projection Target',
      z: 11,
      silent: projectionCfg.silent !== false,
      data: projections.map((projection) => ({
        value: [projection.to.x, projection.to.y],
        label: projection.label,
        from: projection.from,
        itemStyle: {
          color: projection.targetColor || projectionCfg.targetColor || '#0f172a',
          borderColor: projectionCfg.targetBorderColor || '#22c55e',
          borderWidth: Number(projectionCfg.targetBorderWidth || 3),
        },
        symbolSize: Number(projectionCfg.targetSymbolSize || 18),
      })),
    };
  }

  private normalizeResponse(response: any): ZoneScatterResponse {
    const cfg = this.getChartConfig();
    const mapping = cfg?.dataMapping || this.data?.dataMapping;
    if (mapping?.enabled === true) {
      return this.normalizeMappedResponse(response, mapping);
    }

    const pointsPath = String(this.data?.pointsPath || this.data?.dataPath || 'points');
    const projectionsPath = String(this.data?.projectionsPath || 'projections');
    const points = this.getValueByPath(response, pointsPath);
    const projections = this.getValueByPath(response, projectionsPath);
    return {
      points: Array.isArray(points) ? points : Array.isArray(response) ? response : [],
      projections: Array.isArray(projections) ? projections : [],
    };
  }

  private normalizeMappedResponse(response: any, mapping: any): ZoneScatterResponse {
    const rowsPath = String(mapping.rowsPath || this.data?.rowsPath || 'responseList.0.data');
    const mappedRows = this.getValueByPath(response, rowsPath);
    const rows = Array.isArray(mappedRows)
      ? mappedRows
      : Array.isArray(response)
        ? response
        : response && typeof response === 'object'
          ? [response]
          : [];
    if (!Array.isArray(rows)) {
      return { points: [], projections: [] };
    }

    const idKey = String(mapping.idKey || mapping.labelKey || 'customer_name');
    const labelKey = String(mapping.labelKey || idKey);
    const xKey = String(mapping.xKey || 'x');
    const yKey = String(mapping.yKey || 'y');
    const targetYKey = String(mapping.targetYKey || mapping.optimalYKey || '');
    const projectionLabelKey = String(mapping.projectionLabelKey || mapping.adjustmentKey || '');
    const symbolSizeKey = String(mapping.symbolSizeKey || '');
    const bubbleSizeKey = String(mapping.bubbleSizeKey || mapping.sizeKey || '');
    const minSymbolSize = Number(mapping.minSymbolSize ?? mapping.minBubbleSize ?? 8);
    const maxSymbolSize = Number(mapping.maxSymbolSize ?? mapping.maxBubbleSize ?? 24);
    const colorKey = String(mapping.colorKey || '');
    const bubbleSizeValues = bubbleSizeKey
      ? rows
        .map((row: any) => Number(row?.[bubbleSizeKey]))
        .filter((value: number) => Number.isFinite(value))
      : [];
    const minBubbleValue = bubbleSizeValues.length ? Math.min(...bubbleSizeValues) : 0;
    const maxBubbleValue = bubbleSizeValues.length ? Math.max(...bubbleSizeValues) : 0;

    const points: ZoneScatterPoint[] = rows
      .map((row: any, index: number) => {
        const x = Number(row?.[xKey]);
        const y = Number(row?.[yKey]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          return null;
        }

        const id = String(row?.[idKey] ?? row?.[labelKey] ?? `point-${index + 1}`);
        const point: ZoneScatterPoint = {
          ...row,
          id,
          label: String(row?.[labelKey] ?? id),
          x,
          y,
        };

        if (bubbleSizeKey && row?.[bubbleSizeKey] !== undefined) {
          point.symbolSize = this.scaleBubbleSymbolSize(
            Number(row[bubbleSizeKey]),
            minBubbleValue,
            maxBubbleValue,
            minSymbolSize,
            maxSymbolSize,
          );
        } else if (symbolSizeKey && row?.[symbolSizeKey] !== undefined) {
          point.symbolSize = Number(row[symbolSizeKey]);
        }

        if (colorKey && row?.[colorKey]) {
          point.color = String(row[colorKey]);
        }

        return point;
      })
      .filter((point): point is ZoneScatterPoint => !!point);

    const projections: ZoneScatterProjection[] = targetYKey
      ? points
        .map((point): ZoneScatterProjection | null => {
          const targetY = Number(point[targetYKey]);
          if (!Number.isFinite(targetY)) {
            return null;
          }

          const label = projectionLabelKey && point[projectionLabelKey] !== undefined
            ? this.formatProjectionDelta(Number(point[projectionLabelKey]), mapping.projectionLabelSuffix || '%', mapping.projectionLabelDecimals)
            : undefined;

          return {
            fromPointId: String(point.id || point.label || ''),
            to: { x: Number(point.x), y: targetY },
            label,
          };
        })
        .filter((projection): projection is ZoneScatterProjection => !!projection)
      : [];

    return { points, projections };
  }

  private scaleBubbleSymbolSize(
    value: number,
    minValue: number,
    maxValue: number,
    minSymbolSize: number,
    maxSymbolSize: number,
  ): number | undefined {
    if (!Number.isFinite(value)) return undefined;

    const safeMinSize = Number.isFinite(minSymbolSize) ? minSymbolSize : 8;
    const safeMaxSize = Number.isFinite(maxSymbolSize) ? maxSymbolSize : 24;
    if (maxValue <= minValue) {
      return Math.round((safeMinSize + safeMaxSize) / 2);
    }

    const ratio = (value - minValue) / (maxValue - minValue);
    const clampedRatio = Math.min(1, Math.max(0, ratio));
    return Math.round(safeMinSize + clampedRatio * (safeMaxSize - safeMinSize));
  }

  private getChartConfig(): any {
    return this.data?.chartConfig && typeof this.data.chartConfig === 'object' ? this.data.chartConfig : {};
  }

  private normalizeAxis(axis: any, fallback: ZoneScatterAxisConfig): ZoneScatterAxisConfig {
    return {
      ...fallback,
      ...(axis || {}),
      type: String(axis?.type || fallback.type || 'value'),
      min: Number(axis?.min ?? fallback.min),
      max: Number(axis?.max ?? fallback.max),
      onZero: axis?.onZero ?? fallback.onZero ?? false,
      segments: Array.isArray(axis?.segments) ? axis.segments : [],
    };
  }

  private normalizeZones(cfg: any, xAxis: ZoneScatterAxisConfig, yAxis: ZoneScatterAxisConfig): ZoneScatterZoneConfig[] {
    const stepZones = cfg.stepZones;
    if (stepZones?.enabled === true && Array.isArray(stepZones.steps)) {
      const safeCfg = stepZones.safeZone || {};
      const riskCfg = stepZones.riskZone || stepZones.unsafeZone || {};
      const totalSteps = stepZones.steps.length;
      const zones: ZoneScatterZoneConfig[] = [];

      stepZones.steps.forEach((step: any, index: number) => {
        const xRange = Array.isArray(step.xRange)
          ? step.xRange
          : [Number(step.xStart ?? xAxis.min), Number(step.xEnd ?? xAxis.max)];
        const safeYEnd = Number(step.safeYEnd ?? step.yEnd ?? step.threshold ?? yAxis.segments?.[index]?.end ?? yAxis.max);
        if (!Number.isFinite(xRange[0]) || !Number.isFinite(xRange[1]) || !Number.isFinite(safeYEnd)) {
          return;
        }

        const safeTitlePosition = String(safeCfg.titlePosition || 'top-left');
        const showSafeOnLastStep = safeTitlePosition.includes('right');
        const safeTitleStepIndex = showSafeOnLastStep ? totalSteps - 1 : 0;

        zones.push({
          id: `safe-step-${index}`,
          title: safeCfg.showTitle === true && index === safeTitleStepIndex ? String(safeCfg.title || '') : '',
          xRange: [xRange[0], xRange[1]],
          yRange: [yAxis.min, safeYEnd],
          backgroundColor: step.safeBackgroundColor || safeCfg.backgroundColor,
          textColor: safeCfg.textColor,
          fontSize: safeCfg.fontSize,
          fontWeight: safeCfg.fontWeight,
          titlePosition: safeCfg.titlePosition,
        });

        zones.push({
          id: `risk-step-${index}`,
          title: riskCfg.showTitle === false || index !== 0 ? '' : String(riskCfg.title || ''),
          icon: riskCfg.icon,
          xRange: [xRange[0], xRange[1]],
          yRange: [safeYEnd, yAxis.max],
          backgroundColor: step.riskBackgroundColor || riskCfg.backgroundColor,
          textColor: riskCfg.textColor,
          fontSize: riskCfg.fontSize,
          fontWeight: riskCfg.fontWeight,
          titlePosition: riskCfg.titlePosition,
        });
      });

      return zones;
    }

    const rawZones = cfg.zones;
    if (Array.isArray(rawZones)) return rawZones;
    if (rawZones && typeof rawZones === 'object') {
      return Object.keys(rawZones).map((key) => {
        const zone = rawZones[key] || {};
        return {
          id: key,
          title: zone.title || key,
          xRange: zone.xRange || [xAxis.min, xAxis.max],
          yRange: zone.yRange || [yAxis.min, yAxis.max],
          ...zone,
        };
      });
    }
    return [];
  }

  private getAxisBoundaries(axis: ZoneScatterAxisConfig): number[] {
    const boundaries = new Set<number>([axis.min, axis.max]);
    (axis.segments || []).forEach((segment) => {
      boundaries.add(Number(segment.start));
      boundaries.add(Number(segment.end));
    });
    return [...boundaries].filter((value) => Number.isFinite(value));
  }

  private resolveZoneTitlePoint(zone: ZoneScatterZoneConfig, x: number, y: number, width: number, height: number): any {
    const pad = 16;
    switch (zone.titlePosition || 'top-left') {
      case 'top-center':
        return { x: x + width / 2, y: y + pad, align: 'center' };
      case 'top-right':
        return { x: x + width - pad, y: y + pad, align: 'right' };
      case 'center':
        return { x: x + width / 2, y: y + height / 2, align: 'center' };
      case 'bottom-left':
        return { x: x + pad, y: y + height - pad, align: 'left' };
      case 'bottom-right':
        return { x: x + width - pad, y: y + height - pad, align: 'right' };
      case 'top-left':
      default:
        return { x: x + pad, y: y + pad, align: 'left' };
    }
  }

  private findPointById(points: ZoneScatterPoint[], id?: string): ZoneScatterPoint | null {
    if (!id) return null;
    return points.find((point) => String(point.id || '') === String(id)) || null;
  }

  private bindChartEvents(): void {
    if (!this.myChart) return;
    this.myChart.getZr().off('click', this.handleCanvasClick);
    this.myChart.getZr().on('click', this.handleCanvasClick);
  }

  private handleCanvasClick = (event: any): void => {
    if (!this.myChart || !this.lastResponse) {
      return;
    }

    const points = Array.isArray(this.lastResponse.points) ? this.lastResponse.points : [];
    const cfg = this.getChartConfig();
    const pointStyle = cfg.point || {};
    const defaultSymbolSize = Number(pointStyle.symbolSize || 18);
    const clickPixel = [Number(event?.offsetX), Number(event?.offsetY)];
    if (!Number.isFinite(clickPixel[0]) || !Number.isFinite(clickPixel[1])) {
      return;
    }

    const clickedPoint = points
      .map((point) => {
        const pixel = this.myChart?.convertToPixel({ gridIndex: 0 }, [Number(point.x), Number(point.y)]) as number[] | undefined;
        if (!Array.isArray(pixel)) return null;
        const dx = pixel[0] - clickPixel[0];
        const dy = pixel[1] - clickPixel[1];
        return {
          point,
          distance: Math.hypot(dx, dy),
          hitRadius: Math.max(14, Number(point.symbolSize || defaultSymbolSize) / 2 + 8),
        };
      })
      .filter((entry): entry is { point: ZoneScatterPoint; distance: number; hitRadius: number } => !!entry)
      .sort((a, b) => a.distance - b.distance)[0];

    if (!clickedPoint || clickedPoint.distance > clickedPoint.hitRadius) {
      return;
    }

    const pointId = String(clickedPoint.point.id || clickedPoint.point.label || '');
    if (!pointId) {
      return;
    }

    if (this.handlePointNavigation(clickedPoint.point)) {
      return;
    }

    this.activeProjectionPointId = this.activeProjectionPointId === pointId ? null : pointId;
    this.renderChart(this.lastResponse);
  };

  private handlePointNavigation(point: ZoneScatterPoint): boolean {
    if (!this.data?.navigateAction && !this.data?.navigateTo) {
      return false;
    }

    const navigationPoint = this.buildNavigationPoint(point);
    const pointName = String(navigationPoint.label || navigationPoint['customer_name'] || navigationPoint.id || '');
    const params = {
      name: pointName,
      value: [Number(point.x), Number(point.y)],
      data: {
        ...navigationPoint,
        raw: navigationPoint,
      },
      componentType: 'series',
      seriesName: this.legendLabel,
    };

    const filters = this.globalFilterService.getCurrentFilters?.() || {};
    return executeNavigationFromConfig(this.router, this.data, params, {
      event: {
        name: params.name,
        value: params.value,
        raw: navigationPoint,
        seriesName: params.seriesName,
        componentType: params.componentType,
      },
      data: this.data || {},
      filters,
      context: {
        widgetId: this.data?.widgetId,
        customer_name: navigationPoint['customer_name'],
        customerName: navigationPoint['customerName'],
        filters,
      },
      widgetId: this.data?.widgetId,
    });
  }

  private buildNavigationPoint(point: ZoneScatterPoint): ZoneScatterPoint {
    const customerName = this.firstNonEmpty(
      point['customer_name'],
      point['customerName'],
      point['customer'],
      point['name'],
      point.label,
      point.id,
    );
    const label = this.firstNonEmpty(point.label, customerName, point.id);
    return {
      ...point,
      label,
      name: this.firstNonEmpty(point['name'], label),
      rawLabel: this.firstNonEmpty(point['rawLabel'], label),
      customer_name: customerName,
      customerName,
    };
  }

  private firstNonEmpty(...values: any[]): string {
    const match = values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
    return match === undefined ? '' : String(match);
  }

  private resolveProjections(
    cfg: any,
    xAxis: ZoneScatterAxisConfig,
    yAxis: ZoneScatterAxisConfig,
    points: ZoneScatterPoint[],
    projections: ZoneScatterProjection[],
  ): Array<ZoneScatterProjection & { from: { x: number; y: number }; to: { x: number; y: number }; label: string }> {
    const projectionCfg = cfg?.projection || {};
    const stepZones = cfg?.stepZones;
    const autoFromStepZones = projectionCfg.autoFromStepZones === true;
    const fallbackToStepZones = stepZones?.enabled === true && projectionCfg.fallbackToStepZones !== false;
    const computed: Array<ZoneScatterProjection & { from: { x: number; y: number }; to: { x: number; y: number }; label: string; sourcePointId?: string }> = [];
    const projectionInputs: ZoneScatterProjection[] = autoFromStepZones
      ? points.map((point) => ({
        fromPointId: String(point.id || point.label || ''),
        to: { x: Number(point.x), y: Number(point.y) },
      } as ZoneScatterProjection))
      : projections;

    projectionInputs.forEach((projection) => {
      const fromPoint = projection.from || this.findPointById(points, projection.fromPointId);
      if (!fromPoint) return;

      const source = { x: Number(fromPoint.x), y: Number(fromPoint.y) };
      if (!Number.isFinite(source.x) || !Number.isFinite(source.y)) return;

      let target = projection.to ? { x: Number(projection.to.x), y: Number(projection.to.y) } : null;

      if (stepZones?.enabled === true && (autoFromStepZones || projectionCfg.snapTargetToStepZone === true)) {
        const safeTarget = this.resolveStepTargetForPoint(source, cfg, xAxis, yAxis);
        if (!safeTarget && autoFromStepZones) return;
        if (safeTarget) {
          target = safeTarget;
        }
      }

      if (!target || !Number.isFinite(target.x) || !Number.isFinite(target.y)) return;

      const delta = target.y - source.y;
      if (autoFromStepZones && delta === 0) return;

      computed.push({
        ...projection,
        from: source,
        to: target,
        sourcePointId: String((fromPoint as any).id || (fromPoint as any).label || projection.fromPointId || ''),
        label: projection.label || this.formatProjectionDelta(delta, projectionCfg.deltaLabelSuffix || '%', projectionCfg.deltaDecimals),
      });
    });

    if (fallbackToStepZones) {
      points.forEach((point) => {
        const pointId = String(point.id || point.label || '');
        if (!pointId) return;

        const hasDownwardProjection = computed.some((projection) => {
          const sourcePointId = String((projection as any).sourcePointId || projection.fromPointId || '');
          return sourcePointId === pointId && Number(projection.from?.y) > Number(projection.to?.y);
        });

        if (hasDownwardProjection) return;

        const source = { x: Number(point.x), y: Number(point.y) };
        if (!Number.isFinite(source.x) || !Number.isFinite(source.y)) return;

        const target = this.resolveStepTargetForPoint(source, cfg, xAxis, yAxis);
        if (!target) return;

        const delta = target.y - source.y;
        computed.push({
          fromPointId: pointId,
          from: source,
          to: target,
          sourcePointId: pointId,
          label: this.formatProjectionDelta(delta, projectionCfg.deltaLabelSuffix || '%', projectionCfg.deltaDecimals),
        });
      });
    }

    return computed;
  }

  private activateSinglePointProjection(
    points: ZoneScatterPoint[],
    projections: Array<ZoneScatterProjection & { from: { x: number; y: number }; to: { x: number; y: number }; label: string }>,
  ): void {
    const projectionCfg = this.getChartConfig()?.projection || {};
    if (projectionCfg.autoShowSingleProjection === false || this.activeProjectionPointId || points.length !== 1) {
      return;
    }

    const singlePointId = String(points[0]?.id || points[0]?.label || '');
    const projection = projections.find((item) => {
      const sourcePointId = String((item as any).sourcePointId || item.fromPointId || '');
      return sourcePointId === singlePointId && Number(item.from?.y) > Number(item.to?.y);
    });

    if (projection) {
      this.activeProjectionPointId = singlePointId;
    }
  }

  private resolveStepTargetForPoint(
    point: { x: number; y: number },
    cfg: any,
    xAxis: ZoneScatterAxisConfig,
    yAxis: ZoneScatterAxisConfig,
  ): { x: number; y: number } | null {
    const stepZones = cfg?.stepZones;
    const steps = Array.isArray(stepZones?.steps) ? stepZones.steps : [];
    if (steps.length === 0) return null;

    const matchedIndex = steps.findIndex((step: any) => {
      const range = Array.isArray(step?.xRange) ? step.xRange : null;
      if (!range || range.length < 2) return false;
      const start = Number(range[0]);
      const end = Number(range[1]);
      return Number.isFinite(start) && Number.isFinite(end) && point.x >= start && point.x <= end;
    });

    const step = steps[matchedIndex >= 0 ? matchedIndex : 0];
    if (!step) return null;

    const safeYEnd = Number(
      step.safeYEnd ??
      step.yEnd ??
      step.threshold ??
      yAxis.segments?.[matchedIndex >= 0 ? matchedIndex : 0]?.end ??
      yAxis.max,
    );

    if (!Number.isFinite(safeYEnd)) return null;
    if (point.y <= safeYEnd) return null;

    return { x: point.x, y: safeYEnd };
  }

  private formatProjectionDelta(delta: number, suffix: string, decimals?: number): string {
    const fixed = Number.isFinite(Number(decimals)) ? Number(decimals) : 0;
    const absoluteText = Math.abs(delta).toFixed(fixed);
    const signed = delta > 0 ? `+${absoluteText}` : delta < 0 ? `-${absoluteText}` : absoluteText;
    return `${signed}${suffix}`;
  }

  private getValueByPath(source: any, path: string): any {
    if (!path) return source;
    return String(path).split('.').reduce((acc: any, part: string) => (acc == null ? undefined : acc[part]), source);
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
